import { useEffect, useRef, useState } from 'react'
import type { CrmDataState } from '../../hooks/useCrmData'
import { WolfMark } from '../brand/WolfMark'

const STANDARD_MINIMUM_MS = 3_000
const STANDARD_EXIT_MS = 200
const REDUCED_MINIMUM_MS = 180
const REDUCED_EXIT_MS = 120

type IntroRevealProps = {
  dataStatus: CrmDataState['status']
  onComplete: () => void
}

function prefersReducedMotion() {
  return (
    window.matchMedia?.('(prefers-reduced-motion: reduce)')
      .matches ?? false
  )
}

export function IntroReveal({
  dataStatus,
  onComplete,
}: IntroRevealProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null)

  const [reducedMotion] = useState(prefersReducedMotion)
  const [minimumElapsed, setMinimumElapsed] = useState(false)
  const [exiting, setExiting] = useState(false)
  const [progress, setProgress] = useState(
    reducedMotion ? 100 : 1,
  )

  const waitingForData =
    minimumElapsed &&
    dataStatus === 'loading' &&
    !exiting

  useEffect(() => {
    const overlay = overlayRef.current
    if (!overlay) return

    const preventBackgroundScroll = (event: Event) =>
      event.preventDefault()

    overlay.addEventListener(
      'touchmove',
      preventBackgroundScroll,
      { passive: false },
    )

    overlay.addEventListener(
      'wheel',
      preventBackgroundScroll,
      { passive: false },
    )

    return () => {
      overlay.removeEventListener(
        'touchmove',
        preventBackgroundScroll,
      )

      overlay.removeEventListener(
        'wheel',
        preventBackgroundScroll,
      )
    }
  }, [])

  useEffect(() => {
    if (reducedMotion) {
      setProgress(100)
      return
    }

    const startedAt = performance.now()
    let frame = 0

    const updateProgress = (now: number) => {
      const elapsed = now - startedAt

      const nextProgress = Math.min(
        100,
        Math.max(
          1,
          Math.floor(
            (elapsed / STANDARD_MINIMUM_MS) * 100,
          ),
        ),
      )

      setProgress((current) =>
        current === nextProgress
          ? current
          : nextProgress,
      )

      if (nextProgress < 100) {
        frame =
          window.requestAnimationFrame(updateProgress)
      }
    }

    frame =
      window.requestAnimationFrame(updateProgress)

    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [reducedMotion])

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        setProgress(100)
        setMinimumElapsed(true)
      },
      reducedMotion
        ? REDUCED_MINIMUM_MS
        : STANDARD_MINIMUM_MS,
    )

    return () => window.clearTimeout(timer)
  }, [reducedMotion])

  useEffect(() => {
    const shouldExit =
      dataStatus === 'error' ||
      (minimumElapsed && dataStatus === 'ready')

    if (shouldExit) {
      setExiting(true)
    }
  }, [dataStatus, minimumElapsed])

  useEffect(() => {
    if (!exiting) return

    const timer = window.setTimeout(
      onComplete,
      reducedMotion
        ? REDUCED_EXIT_MS
        : STANDARD_EXIT_MS,
    )

    return () => window.clearTimeout(timer)
  }, [exiting, onComplete, reducedMotion])

  return (
    <div
      aria-atomic="true"
      aria-label={
        waitingForData
          ? 'Preparing workspace'
          : 'Opening Wolf Forge CRM'
      }
      aria-live="polite"
      className={`forge-reveal${
        waitingForData ? ' is-waiting' : ''
      }${exiting ? ' is-exiting' : ''}${
        reducedMotion
          ? ' is-reduced-motion'
          : ''
      }`}
      ref={overlayRef}
      role="status"
    >
      <div className="forge-reveal__content">
        <div className="forge-reveal__mark">
          <WolfMark
            className="forge-reveal__logo"
            title="Wolf Forge"
          />

          <span
            aria-hidden="true"
            className="forge-reveal__highlight"
          />
        </div>

        <div className="forge-reveal__wordmark">
          WILLIAM'S TREASURE
        </div>

        <div
          aria-hidden="true"
          className="forge-reveal__line"
        >
          <span
            className="forge-reveal__line-fill"
            style={{
              transform: `scaleX(${progress / 100})`,
            }}
          />
        </div>

        <div
          aria-hidden="true"
          className="forge-reveal__percent"
        >
          {progress}%
        </div>

        <p
          aria-hidden={!waitingForData}
          className={`forge-reveal__preparing${
            waitingForData ? ' is-visible' : ''
          }`}
        >
          Preparing workspace...
        </p>
      </div>
    </div>
  )
}

